import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCrown,
    faArrowLeft,
    faInfoCircle,
    faCheckCircle,
    faRocket,
    faCog,
    faBullhorn,
    faChartLine,
    faPoll,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function UsuariosDueño() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                            <FontAwesomeIcon icon={faCrown} className="text-yellow-600 text-3xl" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-gray-800 mb-2">
                                Dueño de la Marca
                            </h1>
                            <p className="text-xl text-gray-600">
                                Usuario principal con acceso completo a todas las funcionalidades de PetitPrime.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-yellow-600" />
                        ¿Qué es un Dueño de la Marca?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        Como <strong>Dueño de la Marca</strong>, eres el usuario principal de PetitPrime con acceso completo a todas las funcionalidades de la plataforma. Tienes el control total sobre tu negocio, desde la configuración inicial hasta el análisis avanzado y la gestión de marketing.
                    </p>
                    <p className="text-gray-700">
                        Este es el tipo de usuario que se crea cuando te registras por primera vez en PetitPrime y completas el proceso de onboarding. Toda la documentación que has visto anteriormente está diseñada para ti como dueño de la marca.
                    </p>
                </div>

                {/* Acceso */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        ¿Cómo acceder?
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Si eres dueño de la marca, accedes a PetitPrime de la siguiente manera:
                        </p>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                            <li>Ve a la página de inicio de PetitPrime</li>
                            <li>Haz clic en <strong>"Iniciar Sesión"</strong> o <strong>"Registrarse"</strong></li>
                            <li>Si es tu primera vez, completa el proceso de registro y onboarding</li>
                            <li>Si ya tienes cuenta, inicia sesión con tu email y contraseña</li>
                            <li>Serás redirigido automáticamente a tu dashboard principal</li>
                        </ol>
                        <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>Nota:</strong> El proceso de registro y onboarding está documentado en la sección <strong>"Quick Start"</strong> de esta documentación.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Funcionalidades Principales */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        Funcionalidades Principales
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Como dueño de la marca, tienes acceso a todas las funcionalidades organizadas en las siguientes categorías:
                        </p>

                        <div className="space-y-6">
                            {/* Quick Start */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faRocket} className="mr-2 text-blue-600" />
                                    Quick Start
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Proceso inicial de configuración:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Registro y creación de cuenta</li>
                                    <li>Verificación de email</li>
                                    <li>Proceso de onboarding (marca, buyer persona, tienda, objetivos)</li>
                                </ul>
                                <Link to="/documentation/quick-start" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                                    Ver documentación completa →
                                </Link>
                            </div>

                            {/* Gestión */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faCog} className="mr-2 text-green-600" />
                                    Gestión
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Administración completa de tu negocio:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Gestión de marcas y tiendas</li>
                                    <li>Gestión de clientes y puntos</li>
                                    <li>Gestión de productos e inventario</li>
                                    <li>Importación y análisis de ventas</li>
                                </ul>
                                <Link to="/documentation/gestion" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                                    Ver documentación completa →
                                </Link>
                            </div>

                            {/* Marketing */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-purple-600" />
                                    Marketing
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Herramientas de marketing y fidelización:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Gestión de ofertas promocionales</li>
                                    <li>Sistema de WinBack para recuperar clientes</li>
                                    <li>Gestión de recompensas y premios</li>
                                    <li>Email Marketing y campañas</li>
                                    <li>Generación de infografías</li>
                                </ul>
                                <Link to="/documentation/marketing" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                                    Ver documentación completa →
                                </Link>
                            </div>

                            {/* Analytics */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-indigo-600" />
                                    Analytics
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Análisis y reportes de tu negocio:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Dashboard Analytics con KPIs</li>
                                    <li>Estadísticas de clientes</li>
                                    <li>Estadísticas de tiendas</li>
                                    <li>Métricas de ofertas</li>
                                    <li>Centro de Reporting ejecutivo</li>
                                </ul>
                                <Link to="/documentation/analytics" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                                    Ver documentación completa →
                                </Link>
                            </div>

                            {/* Feedback y Alertas */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faPoll} className="mr-2 text-pink-600" />
                                    Feedback y Alertas
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Recopilación de feedback y gestión de alertas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Creación y gestión de encuestas</li>
                                    <li>Centro de alertas automáticas</li>
                                    <li>Notificaciones de eventos importantes</li>
                                </ul>
                                <Link to="/documentation/feedback" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                                    Ver documentación completa →
                                </Link>
                            </div>

                            {/* Gestión de Usuarios */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faUsers} className="mr-2 text-teal-600" />
                                    Gestión de Usuarios
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Asignación y gestión de trabajadores y franquiciados:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Agregar trabajadores a tiendas</li>
                                    <li>Agregar franquiciados a tiendas</li>
                                    <li>Gestionar permisos y accesos</li>
                                    <li>Ver quién tiene acceso a cada tienda</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    Esta funcionalidad está disponible desde la página de gestión de tiendas.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Diferencias con Otros Usuarios */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        ¿Qué te diferencia de otros usuarios?
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Control Total:</strong> Solo tú puedes crear y gestionar marcas, configurar tiendas, y asignar trabajadores o franquiciados.
                                </div>
                            </div>
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Acceso Completo:</strong> Tienes acceso a todas las funcionalidades de marketing, analytics, y gestión que los otros tipos de usuarios no tienen.
                                </div>
                            </div>
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Configuración:</strong> Puedes configurar programas de puntos, recompensas, ofertas, y todas las características avanzadas de la plataforma.
                                </div>
                            </div>
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Análisis Completo:</strong> Tienes acceso a todos los reportes, estadísticas y análisis de todas tus marcas y tiendas.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        Mejores Prácticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Completa el onboarding:</strong> Asegúrate de completar todos los pasos del onboarding para configurar correctamente tu marca, buyer persona, tienda y objetivos.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Asigna trabajadores y franquiciados:</strong> No intentes hacer todo tú solo. Asigna trabajadores para las operaciones diarias y franquiciados para gestionar sus tiendas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Revisa Analytics regularmente:</strong> Usa los reportes y estadísticas para tomar decisiones informadas sobre tu negocio.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Configura alertas:</strong> Mantente informado sobre eventos importantes como cumpleaños de clientes y objetivos alcanzados.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Usa el marketing:</strong> Aprovecha las herramientas de marketing para aumentar ventas y fidelizar clientes.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/usuarios"
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Tipos de Usuarios
                    </Link>
                    <Link
                        to="/documentation/usuarios/trabajador"
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                        Siguiente: Trabajador
                        <FontAwesomeIcon icon={faArrowLeft} className="ml-2 rotate-180" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default UsuariosDueño;

